import "./index.scss";

import { useState, useEffect, type ReactNode } from "react";
import { useIntl, FormattedMessage } from "react-intl";
import { useOutlet } from "react-router-dom";

import {
  CompletionRequirement,
  OrganizationTransactionDetailedStatus,
  UserRole,
} from "graphql_globals";
import AlertMessage from "common/core/alert_message";
import { usePermissions } from "common/core/current_user_role";
import Link from "common/core/link";
import { getDocumentFromId } from "util/document_bundle";
import { useA11y } from "common/accessibility";
import { useDocumentTitles } from "util/document_title";
import { isNotaryDocumentDownloadProhibited } from "common/notary/capacity";

import DocumentsListWithActions from "./list";
import MeetingDocument from "./meeting_document";
import SupplementaryDocument from "./supplementary_document";
import type { DocumentBundleForTransactionDetails } from "./index_fragment.graphql";

type Props = {
  viewer: {
    user: null | { notaryProfile?: Parameters<typeof isNotaryDocumentDownloadProhibited>[0] };
  };
  bundle: DocumentBundleForTransactionDetails;
  refetch: () => void;
  viewable?: boolean;
  shareable?: boolean;
  showMergedDocs?: boolean;
  showHeader?: boolean;
  finalizedDocumentOnly?: boolean;
  readOnly?: boolean;
  showNotarialActs?: boolean;
  showResponsiveView?: boolean;
};
type SelectedSupplementaryDoc = { name: string; url: string };

function BundleDocumentListContainer(props: Props) {
  const { bundle, shareable, showHeader, refetch, viewer } = props;
  const { completionRequirements, documents } = bundle;
  const outlet = useOutlet();
  const intl = useIntl();
  useA11y().useDocumentEntitler({
    priority: "modal",
    title: intl.formatMessage(useDocumentTitles().documentDetailsDocument),
  });
  const { currentUserRole, hasPermissionFor } = usePermissions();
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
  const [lastOpenedId, setLastOpenedId] = useState<string | null>(null);
  const [selectedSupplementaryDoc, setSelectedSupplementaryDoc] =
    useState<SelectedSupplementaryDoc | null>(null);

  useEffect(() => {
    refetch();
  }, []);

  if (
    isNotaryDocumentDownloadProhibited(viewer.user?.notaryProfile, bundle.organizationTransaction)
  ) {
    return null;
  }

  const downloadable = hasPermissionFor("downloadTransactionDocuments");

  // don't show to signer or keystone users
  const showAlertsToOrgMembers =
    currentUserRole === UserRole.ORGANIZATION_MEMBER &&
    hasPermissionFor("viewOrganizationTransactions");
  const showAwaitingPaymentAlert =
    showAlertsToOrgMembers &&
    bundle.transaction.detailedStatus === OrganizationTransactionDetailedStatus.AWAITING_PAYMENT;
  const showPaymentFailedAlert =
    showAlertsToOrgMembers &&
    bundle.transaction.detailedStatus === OrganizationTransactionDetailedStatus.PAYMENT_REQUIRED;

  const selectedDocument = selectedDocumentId && getDocumentFromId(documents, selectedDocumentId);
  if (selectedDocument) {
    return (
      <>
        {outlet ? (
          outlet
        ) : (
          <MeetingDocument
            document={selectedDocument}
            bundle={bundle}
            backPath={() => {
              setLastOpenedId(selectedDocument.id);
              setSelectedDocumentId(null);
            }}
            shareable={shareable}
            downloadable={downloadable}
            finalizedDocumentOnly={props.finalizedDocumentOnly}
            onDocumentSelected={(docId) => {
              setSelectedDocumentId(docId);
              setLastOpenedId(docId);
            }}
            showResponsiveView={props.showResponsiveView}
          />
        )}
      </>
    );
  }

  if (selectedSupplementaryDoc && downloadable) {
    return (
      <SupplementaryDocument
        document={selectedSupplementaryDoc}
        backPath={() => setSelectedSupplementaryDoc(null)}
        downloadable={downloadable}
        showResponsiveView={props.showResponsiveView}
      />
    );
  }

  return (
    <>
      {showAwaitingPaymentAlert && (
        <AlertMessage banner kind="info" centerText>
          <FormattedMessage
            id="115abf73-688c-4c08-b39c-c5dba29acb07"
            defaultMessage="This transaction is awaiting payment. Documents can be accessed once the signer completes payment."
          />
        </AlertMessage>
      )}
      {showPaymentFailedAlert && (
        <AlertMessage banner kind="warning" centerText>
          <FormattedMessage
            id="fd68ce3e-489f-4434-8cd6-35522fdaaef8"
            defaultMessage="Payment failed. Documents can be accessed once payment is complete. <link>Update payment details</link>"
            values={{
              link: (text: ReactNode) => (
                <Link href="/settings/billing/payment-settings" openInCurrentTab={false}>
                  {text}
                </Link>
              ),
            }}
          />
        </AlertMessage>
      )}
      {outlet ? (
        outlet
      ) : (
        <DocumentsListWithActions
          bundle={bundle}
          viewable={props.viewable && !props.readOnly}
          shareable={shareable}
          showMergedDocs={props.showMergedDocs}
          downloadable={downloadable}
          onDocumentSelected={(doc) => setSelectedDocumentId(doc.id)}
          onSupplementaryDocSelected={setSelectedSupplementaryDoc}
          showHeader={showHeader}
          showLegalInfoItem={Boolean(
            completionRequirements?.includes(CompletionRequirement.NOTARIZATION),
          )}
          showNotarialActs={props.showNotarialActs}
          lastOpenedId={lastOpenedId}
          showResponsiveView={props.showResponsiveView}
        />
      )}
    </>
  );
}

BundleDocumentListContainer.defaultProps = {
  viewable: true,
  showHeader: true,
  showMergedDocs: true,
};

export default BundleDocumentListContainer;
